/* =====================================================================
 * WorkspaceModeContent — concrete page renderer for workspace modes.
 *
 * This is intentionally chrome-free. Legacy WorkspaceRouter and new
 * route-first layout elements both call this renderer.
 * ===================================================================== */

import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Construction } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { WORKSPACE_MODES, type WorkspaceMode } from '@auw/workspace-utils';
import { useFeatureFlags, type FeatureId } from '@auw/feature-flags';
import { WorkspaceModeFrame } from '../workspace/shell/WorkspaceModeFrame';
import { WorkspaceSplitPane } from '../workspace/panes/WorkspaceSplitPane';
import { AgentModePage } from '../modes/agent/AgentModePage';
import { LiveAgentDock } from '../modes/live-agent/LiveAgentDock';
import DocumentModePage from '../modes/document/DocumentModePage';
import {
  getDocAgentEditorPageComponent,
  getDocAgentLandingPageComponent,
  getDocAgentModePageComponent,
} from '../modes/doc-agent/docAgentSlot';
import { useWorkspaceRoute } from '../workspace/navigation/useWorkspaceRoute';

const LiveAgentPage = lazy(() =>
  import('../modes/live-agent/LiveAgentPage').then((m) => ({ default: m.LiveAgentPage })),
);
const DMPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.DMPage })));
const AppManagePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.AppManagePage })));
const AdminPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.AdminPage })));
const PluginPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.PluginPage })));
const SkillCreatePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.SkillCreatePage })));
const StoragePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.StoragePage })));
const WorkspaceModeStartPage = lazy(() =>
  import('../workspace/shell/WorkspaceModeStartPage').then((m) => ({ default: m.WorkspaceModeStartPage })),
);
const WidgetPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.WidgetPage })));
const FeedPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.FeedPage })));
const ToolsPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.ToolsPage })));

const PlaceholderWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const PlaceholderLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PlaceholderSub = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ContentFallback = styled.div`
  flex: 1;
  min-height: 0;
`;

const PlaceholderPage: React.FC<{ mode: WorkspaceMode }> = ({ mode }) => {
  const { t } = useTranslation();
  const info = WORKSPACE_MODES[mode];
  const ModeIcon = info?.icon ?? Construction;
  return (
    <PlaceholderWrap>
      <PlaceholderIcon>
        <ModeIcon size={32} />
      </PlaceholderIcon>
      <PlaceholderLabel>{info ? t(info.labelKey) : mode}</PlaceholderLabel>
      <PlaceholderSub>{t('common:chat.preparing')}</PlaceholderSub>
    </PlaceholderWrap>
  );
};

export interface WorkspaceModeContentProps {
  readonly mode?: WorkspaceMode;
  readonly inputSlot?: React.ReactNode;
  readonly docAgentView?: 'auto' | 'landing' | 'editor';
}

export const WorkspaceModeContent: React.FC<WorkspaceModeContentProps> = ({
  mode,
  inputSlot,
  docAgentView = 'auto',
}) => {
  const activeModeFallback = useChatStore((s) => s.activeMode);
  const route = useWorkspaceRoute();
  const activeMode = mode ?? route.mode ?? activeModeFallback;
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const modeFeatureId = `mode.${activeMode}` as FeatureId;
  const isActiveModeEnabled = isFeatureEnabled(modeFeatureId);
  const activeConversation = useChatStore((s) => (
    route.itemId ? (s.conversations[route.itemId] ?? null) : null
  ));
  const activeModeConversation = activeConversation?.mode === activeMode ? activeConversation : null;
  const hasModePages = useChatStore((s) => {
    if (activeMode === 'agent' || activeMode === 'live-agent') return true;
    for (const id of s.conversationOrder) {
      const conv = s.conversations[id];
      if (!conv) continue;
      if (conv.mode === activeMode && conv.workspaceKind !== 'folder') {
        return true;
      }
    }
    return false;
  });

  const showStartPage = !hasModePages || activeModeConversation?.startPagePending === true;

  const renderContent = () => {
    if (!isActiveModeEnabled) {
      return (
        <WorkspaceModeFrame mode={activeMode} inputSlot={inputSlot}>
          <PlaceholderPage mode={activeMode} />
        </WorkspaceModeFrame>
      );
    }

    switch (activeMode) {
      case 'agent':
        return (
          <WorkspaceModeFrame mode="agent" inputSlot={inputSlot}>
            <AgentModePage />
          </WorkspaceModeFrame>
        );

      case 'live-agent':
        return (
          <WorkspaceModeFrame mode="live-agent" aboveInputSlot={<LiveAgentDock />}>
            <LiveAgentPage />
          </WorkspaceModeFrame>
        );

      case 'document':
        return (
          <WorkspaceModeFrame mode="document" inputSlot={inputSlot}>
            {showStartPage ? (
              <WorkspaceModeStartPage mode="document" />
            ) : (
              <DocumentModePage />
            )}
          </WorkspaceModeFrame>
        );

      case 'doc-agent': {
        if (docAgentView === 'landing') {
          const DocAgentLandingPage = getDocAgentLandingPageComponent();
          if (DocAgentLandingPage) return <DocAgentLandingPage />;
        }

        if (docAgentView === 'editor') {
          const DocAgentEditorPage = getDocAgentEditorPageComponent();
          if (DocAgentEditorPage && route.itemId) {
            return <DocAgentEditorPage itemId={route.itemId} />;
          }
        }

        const DocAgentModePage = getDocAgentModePageComponent();
        if (!DocAgentModePage) {
          return (
            <WorkspaceModeFrame mode="doc-agent" inputSlot={inputSlot}>
              <PlaceholderPage mode="doc-agent" />
            </WorkspaceModeFrame>
          );
        }
        return <DocAgentModePage />;
      }

      case 'storage':
        return <StoragePage />;

      case 'message':
        return <DMPage />;

      case 'widget':
        return <WidgetPage />;

      case 'feed':
        return <FeedPage />;

      case 'tools':
        return <ToolsPage />;

      case 'app-manage':
        return <AppManagePage />;

      case 'admin-page':
        return <AdminPage />;

      case 'plugin':
        return <PluginPage />;

      case 'skill-create':
        return <SkillCreatePage />;

      default:
        return (
          <WorkspaceModeFrame mode={activeMode} inputSlot={inputSlot}>
            <WorkspaceSplitPane
              primaryContent={<PlaceholderPage mode={activeMode} />}
            />
          </WorkspaceModeFrame>
        );
    }
  };

  return (
    <Suspense fallback={<ContentFallback />}>
      {renderContent()}
    </Suspense>
  );
};
